import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Icon from '../../../ui/Icon';
import Button from '../../../ui/Button';
import Badge from '../../../ui/Badge';
import StatusBadge from '../../../ui/Status/StatusBadge';
import Tabs from '../../../ui/Tabs/Tabs';
import EmptyState from '../../../ui/EmptyState';
import { Input } from '../../../ui/Field/Field';
import { toast } from '../../../ui/feedback';
import { ICONS } from '../../../ui/icon-map';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { usePermissions } from '../../../hooks/usePermissions';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellId, CellPlate, CellNumber, toneClass } from '../../../ui/DataTable/cells';
import Card from '../../../ui/Card';
import { useAirportLogic, FILTER_MODES, tripCellOf, chargeCardsOf, liveAt } from './airport.logic';
import Stat, { StatGrid } from '../../../ui/Stat/Stat';
import { formatContractDateTime } from '../../../utils/contractDateUtils';
import ContactPhone from '../../../components/ContactPhone';
import CustomerProfileModal from '../../../components/CustomerProfileModal';
import contactsService, { contractKey, phoneDigits } from '../../../services/contactsService';
import { finesService } from '../../../services/finesService';
import { USE_CONTRACTS_V2 } from '../../../services/contractsV2';

// Pick-up / drop-off as "dd/mm/yyyy HH:MM" (the app's one contract date format): a value
// without a time stays "dd/mm/yyyy" - never an invented 00:00. Unreadable text is shown as written.
const formatDisplayDate = (val) => formatContractDateTime(val) || (typeof val === 'string' ? val : '');

// Salik + Darb and parking are asked again this often while the page is open (owner, 1 Oct 2026: "ويتحدثوا
// باستمرار"); the engine keeps salik.db current and answers from it (kept a minute there).
const TRIPS_EVERY_MS = 2 * 60 * 1000;
const TRIPS_PER_CALL = 500;          // the engine takes at most 600 contracts a call
const PARKING_UNIT = ['parking entry', 'parking entries'];

const TRIPS_KEEP_MS = 60 * 1000;     // a contract read less than a minute ago is not asked again on a filter change

const TripCell = ({ cell }) => {
    if (cell.kind === 'loading') return <span className="ds-cell-empty">…</span>;
    if (cell.kind === 'unknown') return <span className="ds-cell-empty" title={cell.title}>—</span>;
    if (cell.kind === 'zero') return <CellNumber value={0} />;
    return (
        <div className="flex flex-col gap-0.5 items-center" title={cell.title}>
            <Badge variant={cell.variant} size="sm">{cell.n}</Badge>
            <span className="text-[11px] text-gray-700 tabular-nums whitespace-nowrap">{cell.sub}</span>
        </div>
    );
};

// -- the cards above the table (owner, 1 Oct 2026: "كروت فى الصفحة من فوق الداتا لايف لغاية امتى ايه اللى مش متسجل
//    خاصة الفاينز والباركينج على العقود المفتوحه فقط ... وطبعا الكروت تتغير بناءا على الفرع") --------------------------
const AED_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SCOPE = { All: 'Terminal 1 + Terminal 2', T1: 'Terminal 1', T2: 'Terminal 2' };
const OWED_WHAT = { any: 'something', fines: 'fines', parking: 'parking', gates: 'Salik / Darb' };
const OWED_KINDS = ['fines', 'parking', 'gates'];
// the portals are pulled every 30 minutes and RTA every hour (a full read daily): older than this is shown red
const LATE_MIN = { toll: 70, darb: 70, parking: 70, fines: 26 * 60, ejar: 45 };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const ageMin = (iso, now) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(iso || ''));
    return m ? (now - new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0))) / 60000 : null;
};
const owes = (t, kind) => !!t && (kind === 'any' ? OWED_KINDS : [kind]).some((k) => (Number(t[k] && t[k].owed) || 0) > 0.005);

const LiveCard = ({ live, ejar, failed }) => {
    const now = new Date();
    const line = (key, label, text, late) => (
        <div key={key} className="flex justify-between gap-3">
            <span className="text-gray-600">{label}</span>
            <span className={`font-semibold ${late ? 'text-red-700' : 'text-gray-900'}`}>{text}</span>
        </div>
    );
    const source = (k, label) => {
        const off = k === 'darb' && live.darbConnected === false;
        const age = ageMin(live[k], now);
        return line(k, label, `${liveAt(live[k], now)}${off ? ' · not connected' : ''}`, off || age === null || age > LATE_MIN[k]);
    };
    // Ejar: the oldest good check of the open contracts with charges; red when Ejar does not answer (the checks wait a
    // few minutes) or a check is older than LATE_MIN.ejar
    const paused = !!(live && live.ejarCheck && live.ejarCheck.paused);
    const ejarAge = ageMin(ejar.oldest, now);
    const ejarText = paused ? 'Ejar not answering - retrying'
        : ejar.pending > 0 ? `checking ${ejar.pending}…` : ejar.oldest ? liveAt(ejar.oldest, now) : 'nothing to check';
    const ejarLate = paused || (ejar.pending === 0 && ejarAge !== null && ejarAge > LATE_MIN.ejar);
    return (
        <div className="ds-stat" title="When each source was last read: Salik, Darb and parking for the whole fleet, the RTA fines portal, and Ejar for the open contracts with charges (checked every 30 minutes while this page is open)">
            <span className="ds-stat__label"><Icon glyph={ICONS.clock} size="sm" />Data live until</span>
            {!live ? <span className="ds-stat__meta">{failed ? 'could not be loaded' : 'loading…'}</span> : (
                <div className="flex flex-col gap-0.5 text-xs tabular-nums w-full">
                    {source('toll', 'Salik')}
                    {source('darb', 'Darb')}
                    {source('parking', 'Parking')}
                    {source('fines', 'Fines (RTA)')}
                    {line('ejar', 'Checked on Ejar', ejarText, ejarLate)}
                </div>
            )}
        </div>
    );
};

const ChargeCards = ({ cards, live, failed, scope, picked, onPick }) => {
    const tile = (id, label, icon, owed, meta) => {
        const known = owed !== null;
        const due = known && owed > 0.005;
        return (
            <Stat
                key={id} label={label} icon={icon}
                value={known ? `AED ${AED_FMT.format(owed)}` : (failed ? '—' : undefined)}
                loading={!known && !failed}
                tone={known ? (due ? 'danger' : 'success') : undefined}
                meta={known ? meta : undefined}
                onClick={due || picked === id ? () => onPick(picked === id ? null : id) : undefined}
                className={picked === id ? 'is-active' : ''}
                title={due ? (picked === id ? 'Show every contract again' : 'Show only these contracts in the table') : undefined}
            />
        );
    };
    const left = cards.unknown ? ` · ${cards.unknown} not in the engine yet` : '';
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{`Not on Ejar yet · open contracts · ${scope}`}</span>
            <StatGrid min={180}>
                {tile('any', 'Not on Ejar · total', ICONS.alert, cards.total, `${cards.owing} of ${plural(cards.contracts, 'open contract', 'open contracts')}${left}`)}
                {tile('fines', 'Fines', ICONS.fines, cards.fines.owed, `${plural(cards.fines.items, 'fine', 'fines')} · ${plural(cards.fines.contracts, 'contract', 'contracts')}`)}
                {tile('parking', 'Parking', ICONS.car, cards.parking.owed, `${plural(cards.parking.items, 'entry', 'entries')} · ${plural(cards.parking.contracts, 'contract', 'contracts')}`)}
                {tile('gates', 'Salik + Darb', ICONS.salik, cards.gates.owed, plural(cards.gates.contracts, 'contract', 'contracts'))}
                <LiveCard live={live} ejar={cards.ejar} failed={failed} />
            </StatGrid>
        </div>
    );
};

const AirportContracts = ({ onBack }) => {
    // the Refresh starts an Ejar sync: its own permission (permissions phase 4, 4 Oct 2026)
    const canSync = usePermissions().hasPermission('run_ejar_sync');
    const {
        loading, error, data, stats, openContracts, openOfBranch,
        filterMode, setFilterMode,
        customerTypeFilter, setCustomerTypeFilter,
        durationFilter, setDurationFilter,
        branchFilter, setBranchFilter,
        expiryFilter, setExpiryFilter,
        searchTerm, setSearchTerm,
        isPartialClosed, loadAllClosed, closedSince,
        refreshData, refreshOpenQuietly, getExpiryStatus
    } = useAirportLogic();

    const [customerProfile, setCustomerProfile] = useState(null);
    const [contactsTick, setContactsTick] = useState(0);   // bump to re-read the in-memory contacts (id / phone)
    const [fineCounts, setFineCounts] = useState({});      // contractNo -> number of fines
    const [platesByContract, setPlatesByContract] = useState({}); // contractNo -> [plates] (from E-Check)
    const [filling, setFilling] = useState(false);

    // Load the contacts store (for the Customer ID column + which contracts miss a phone).
    useEffect(() => { contactsService.ensureLoaded().then(() => setContactsTick(t => t + 1)).catch(() => {}); }, []);

    // Fine count from the one-time fines index (loaded once, instant lookups after).
    useEffect(() => {
        let alive = true;
        (async () => {
            const { finesIndex } = await import('../../../services/finesIndex');
            try {
                await finesIndex.ensureLoaded();
            } catch (e) {
                // a failed read is not "0 fines": the column stays "…" and says why (tried again on the next load)
                if (alive) { setFineCounts({}); toast.error(`Fines could not be loaded: ${e?.message || 'the fines service did not answer'}`); }
                return;
            }
            if (!alive) return;
            const m = {};
            for (const r of [...(data || []), ...(openContracts || [])]) { const k = contractKey(r.contractNo); if (k) m[k] = finesIndex.countForContract(k) || 0; }
            setFineCounts(m);
        })();
        return () => { alive = false; };
    }, [data, openContracts]);

    // Salik + Darb, parking and fines from the engine for every row shown AND every open contract (the cards), in one
    // call, then again every TRIPS_EVERY_MS while the page is open. A filter or search change asks only for the
    // contracts not read in the last minute. A failed read is not "0": the cells and cards keep what they showed ("…"
    // before the first answer) and the page says so once; a contract the engine does not know shows "—". An answer
    // older than the one already shown for a contract is dropped (two asks can cross).
    const tripsKey = useMemo(
        () => [...new Set([...(data || []), ...(openContracts || [])].map((r) => contractKey(r.contractNo)).filter(Boolean))].sort().join(','),
        [data, openContracts]
    );
    const [trips, setTrips] = useState({});           // CONTRACT -> {gates, parking, fines, ...} | null (unknown)
    const [live, setLive] = useState(null);           // until when each source is live: {toll, darb, parking, fines, darbConnected}
    const [tripsFailed, setTripsFailed] = useState(false);
    const tripsFailToast = React.useRef(false);       // the failure is said once, until an answer comes again
    const tripsAsked = React.useRef({});              // CONTRACT -> when the answer shown was asked (ms)
    const liveAsked = React.useRef(0);
    const tripsBusy = React.useRef(null);             // the full refresh running now (its own mark)
    useEffect(() => {
        if (!tripsKey) return undefined;
        let alive = true;
        const wanted = tripsKey.split(',');
        const load = async (all) => {
            if (document.hidden || (all && tripsBusy.current)) return;   // hidden: asked again when it is looked at
            const asked = Date.now();
            const ask = all ? wanted : wanted.filter((cn) => asked - (tripsAsked.current[cn] || 0) > TRIPS_KEEP_MS);
            if (!ask.length) return;
            const mark = {};
            if (all) tripsBusy.current = mark;
            try {
                const calls = [];
                for (let i = 0; i < ask.length; i += TRIPS_PER_CALL) calls.push(finesService.salikContractsTrips(ask.slice(i, i + TRIPS_PER_CALL)));
                const answers = await Promise.all(calls);
                if (!alive) return;
                const got = {};
                answers.forEach((a) => Object.assign(got, (a && a.contracts) || {}));
                const next = {};
                ask.forEach((cn) => {
                    if ((tripsAsked.current[cn] || 0) > asked) return;
                    tripsAsked.current[cn] = asked;
                    next[cn] = got[cn] || null;
                });
                const fresh = answers.find((a) => a && a.fresh);
                if (fresh && asked >= liveAsked.current) {
                    liveAsked.current = asked;
                    setLive({ ...fresh.fresh, ejarCheck: fresh.ejarCheck || null });
                }
                tripsFailToast.current = false;
                setTripsFailed(false);
                setTrips((prev) => ({ ...prev, ...next }));
            } catch (e) {
                if (!alive) return;
                if (!tripsFailToast.current) toast.error(`Salik / parking / fines could not be loaded: ${e?.response?.data?.detail || e?.message || 'the engine did not answer'}`);
                tripsFailToast.current = true;
                setTripsFailed(true);
            } finally {
                if (tripsBusy.current === mark) tripsBusy.current = null;
            }
        };
        load(false);
        const timer = setInterval(() => load(true), TRIPS_EVERY_MS);
        const onShow = () => { if (!document.hidden) load(false); };
        document.addEventListener('visibilitychange', onShow);
        return () => { alive = false; clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
    }, [tripsKey]);
    const tripsOf = useCallback((contractNo) => trips[contractKey(contractNo)], [trips]);
    const cards = useMemo(() => chargeCardsOf(openOfBranch, trips), [openOfBranch, trips]);
    // a card picked: the table lists exactly the contracts that card counts (open, this branch, owing that kind),
    // the most owed first - the other filters stand aside until it is cleared
    const [picked, setPicked] = useState(null);
    const rows = useMemo(() => {
        if (!picked) return data;
        const owedOf = (r) => {
            const t = trips[contractKey(r.contractNo)];
            return (picked === 'any' ? OWED_KINDS : [picked]).reduce((a, k) => a + (Number(t && t[k] && t[k].owed) || 0), 0);
        };
        return (openOfBranch || []).filter((r) => owes(trips[contractKey(r.contractNo)], picked)).sort((a, b) => owedOf(b) - owedOf(a));
    }, [picked, data, openOfBranch, trips]);

    // All plates used per contract (E-Check reveals car switches); loaded once. The contracts store
    // (USE_CONTRACTS_V2): the checks of the OPEN contracts shown (the only rows that list them), each contract read
    // once, the ones that appear later (a search, a refresh) added to what is known.
    const openKey = USE_CONTRACTS_V2
        ? [...new Set([...(data || []), ...(openContracts || [])].filter((r) => r.status === 'Open' || !r.status).map((r) => contractKey(r.contractNo)).filter(Boolean))].sort().join(',')
        : '';
    const platesRead = React.useRef(new Set());
    useEffect(() => {
        let alive = true;
        const wanted = USE_CONTRACTS_V2 ? (openKey ? openKey.split(',') : []).filter((c) => !platesRead.current.has(c)) : null;
        if (wanted && !wanted.length) return undefined;
        (async () => {
            try {
                const { archiveService } = await import('../../../services/archiveService');
                const all = wanted ? await archiveService.findByContractNumbers(wanted) : await archiveService.load();
                if (!alive) return;
                const m = {};
                for (const rec of all || []) {
                    const cn = contractKey(rec['Contract No.'] || rec['Contract No'] || '');
                    const plate = rec['Plate Number'] || rec['Plate No.'] || '';
                    if (!cn || !plate) continue;
                    (m[cn] = m[cn] || new Set()).add(plate.toString().trim());
                }
                const out = {};
                for (const cn in m) out[cn] = [...m[cn]];
                if (wanted) {
                    wanted.forEach((c) => platesRead.current.add(c));
                    setPlatesByContract((prev) => ({ ...prev, ...out }));
                } else {
                    setPlatesByContract(out);
                }
            } catch (e) { /* ignore */ }
        })();
        return () => { alive = false; };
    }, [openKey]);

    const idOf = useCallback((contractNo) => (contactsService.peek(contractNo) || {}).i || '', [contactsTick]); // eslint-disable-line react-hooks/exhaustive-deps
    const hasPhone = useCallback((row) => !!((contactsService.peek(row.contractNo) || {}).p) || !!phoneDigits(row.phone), [contactsTick]); // eslint-disable-line react-hooks/exhaustive-deps
    const finesOf = useCallback((contractNo) => { const k = contractKey(contractNo); return k in fineCounts ? fineCounts[k] : null; }, [fineCounts]);
    const platesOf = useCallback((row) => {
        // Only OPEN contracts show every switched car; a closed contract shows its own plate.
        const open = row.status === 'Open' || !row.status;
        if (!open) return row.plateNo ? [row.plateNo] : [];
        const l = platesByContract[contractKey(row.contractNo)];
        return (l && l.length) ? l : (row.plateNo ? [row.plateNo] : []);
    }, [platesByContract]);

    const showNotification = (msg) => toast.info(msg);

    const handleCopy = (text, msg) => {
        const value = String(text ?? '');
        if (!value) return;
        const ok = () => showNotification(msg || "Copied!");
        // Legacy fallback: works when navigator.clipboard is unavailable
        // (some browsers/webviews, or a non-focused document) — where the modern
        // API throws or is undefined and nothing gets copied.
        const legacy = () => {
            try {
                const ta = document.createElement('textarea');
                ta.value = value;
                ta.style.position = 'fixed';
                ta.style.top = '-9999px';
                ta.setAttribute('readonly', '');
                document.body.appendChild(ta);
                ta.select();
                const done = document.execCommand('copy');
                document.body.removeChild(ta);
                return done;
            } catch (e) {
                return false;
            }
        };
        try {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(value)
                    .then(ok)
                    .catch(() => { legacy() ? ok() : toast.error("Copy failed"); });
            } else {
                legacy() ? ok() : toast.error("Copy failed");
            }
        } catch (e) {
            legacy() ? ok() : toast.error("Copy failed");
        }
    };

    // How many visible open contracts still have no phone (the number the fill closes).
    const missingCount = useMemo(
        () => (data || []).filter(r => (r.status === 'Open' || !r.status) && !hasPhone(r)).length,
        [data, hasPhone]
    );
    const missingRef = React.useRef(0);
    useEffect(() => { missingRef.current = missingCount; }, [missingCount]);

    // Refresh = ask the engine to fetch the missing phones/ids now, then poll the CONTACTS STORE (where
    // the numbers arrive) so the "missing" counter falls. It used to re-read every open airport contract
    // with the full-page spinner every 20 s for 4 minutes, and kept going after the page was left
    // (CON-22). Now it stops when the page closes; the open rows are re-read once, quietly, at the end.
    const aliveRef = React.useRef(true);
    useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
    const handleRefresh = useCallback(async () => {
        if (filling) return;
        setFilling(true);
        try { await finesService.ejarSyncNow(); showNotification('Fetching the missing numbers from Ejar…'); }
        catch (e) { showNotification('A sync is already running — watching for numbers…'); } // 409: one is in progress
        for (let i = 0; i < 12 && aliveRef.current; i++) {
            await new Promise(res => setTimeout(res, 20000));
            if (!aliveRef.current) return;
            try { contactsService.clearCache(); await contactsService.ensureLoaded(); } catch (e) { /* ignore */ }
            if (!aliveRef.current) return;
            setContactsTick(t => t + 1);
            if (missingRef.current === 0) break;
        }
        if (!aliveRef.current) return;
        try { await refreshOpenQuietly(); } catch (e) { /* ignore */ }
        if (aliveRef.current) setFilling(false);
    }, [filling, refreshOpenQuietly]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <LoadingSpinner />;
    if (error) return <EmptyState icon={ICONS.error} title="Could not load airport contracts" description={/permission|false for '/i.test(String(error?.message)) ? 'You do not have access to these contracts, or the server refused the read. Try again, or ask the admin.' : `Error: ${error.message}`}
        action={<Button size="sm" variant="secondary" onClick={() => refreshData()}>Retry</Button>} />;

    const headers = [
        { key: 'contractNo', label: 'Contract No.' },
        { key: 'bookingNumber', label: 'Booking Number' },
        { key: 'customer', label: 'Customer' },
        { key: 'identity', label: 'Customer ID' },
        { key: 'fines', label: 'Fines' },
        { key: 'gates', label: 'Salik + Darb' },
        { key: 'parking', label: 'Parking' },
        { key: 'phone', label: 'Phone' },
        { key: 'pickupBranch', label: 'Pick-up Branch' },
        { key: 'plateNo', label: 'Plate No.' },
        { key: 'model', label: 'Model' },
        { key: 'pickupDate', label: 'Pick-up Date' },
        { key: 'dropoffDate', label: 'Drop-off Date' },
        { key: 'status', label: 'Status' },
        { key: 'createdBy', label: 'Created By' },
    ];

    // Segmented filter groups (same values and setters as before).
    const filterGroups = [
        {
            id: 'status', label: 'Contract status', value: filterMode, onChange: setFilterMode,
            items: [
                { id: FILTER_MODES.OPEN, label: 'Open', count: stats.open },
                { id: FILTER_MODES.CLOSED, label: 'Closed', count: stats.closed },
            ],
        },
        {
            id: 'type', label: 'Customer type', value: customerTypeFilter, onChange: setCustomerTypeFilter,
            items: [
                { id: 'All', label: 'All', count: stats.typeAll },
                { id: 'Corporate', label: 'Corporate', count: stats.corporate },
                { id: 'Individual', label: 'Individual', count: stats.individual },
            ],
        },
        {
            id: 'branch', label: 'Airport location', value: branchFilter, onChange: setBranchFilter,
            items: [
                { id: 'All', label: 'All', count: stats.branchAll },
                { id: 'T1', label: 'Terminal 1', count: stats.t1 },
                { id: 'T2', label: 'Terminal 2', count: stats.t2 },
            ],
        },
        {
            id: 'expiry', label: 'Expiry', value: expiryFilter, onChange: setExpiryFilter,
            items: [
                { id: 'All', label: 'All', count: stats.expiryAll },
                { id: 'Expired', label: 'Expired', count: stats.expiredCount, icon: <Icon glyph={ICONS.alert} size="xs" tone="danger" /> },
                { id: 'Today', label: 'Today', count: stats.todayCount, icon: <Icon glyph={ICONS.clock} size="xs" tone="warning" /> },
            ],
        },
        {
            id: 'period', label: 'Period', value: durationFilter, onChange: setDurationFilter,
            items: [
                { id: 'All', label: 'All', count: stats.durationAll },
                { id: 'Daily', label: 'Daily', count: stats.daily },
                { id: 'Monthly', label: 'Monthly', count: stats.monthly },
            ],
        },
    ];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
        <div className="flex flex-col w-full gap-3">
            {/* Toolbar */}
            <div className="ds-toolbar">
                <Button variant="ghost" onClick={onBack} size="sm" leadingIcon={<Icon glyph={ICONS.arrowLeft} size="sm" />}>
                    Back
                </Button>
                <h2 className="text-base font-semibold text-gray-900">Airport contracts</h2>
                <span className="ds-toolbar__spacer" />
                {canSync && <Button
                    variant="secondary"
                    onClick={handleRefresh}
                    isLoading={filling}
                    size="sm"
                    leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}
                    title="Fetch the missing customer numbers/IDs from Ejar now"
                >
                    {filling
                        ? `Fetching… ${missingCount} left`
                        : (missingCount > 0 ? `Refresh · ${missingCount} missing` : 'Refresh')}
                </Button>}
            </div>

            {/* What is not on Ejar yet on the open contracts of the branch chosen, and until when the data is live */}
            <ChargeCards cards={cards} live={live} failed={tripsFailed} scope={SCOPE[branchFilter] || SCOPE.All}
                picked={picked} onPick={setPicked} />

            {/* Filters */}
            <Card padding="sm"><div className="flex flex-wrap gap-x-6 gap-y-3 items-start">
                {filterGroups.map((g) => (
                    <div key={g.id} className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{g.label}</span>
                        <Tabs
                            variant="segmented"
                            label={g.label}
                            idPrefix={`airport-${g.id}`}
                            items={g.items}
                            value={g.value}
                            onChange={g.onChange}
                        />
                    </div>
                ))}
            </div></Card>

            {/* Search */}
            <div className="max-w-2xl">
                <Input
                    type="search"
                    size="lg"
                    aria-label="Search airport contracts"
                    prefix={<Icon glyph={ICONS.search} size="sm" />}
                    placeholder="Search: contract · booking · customer · Customer ID · phone (any format) · plate"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Partial Load Warning */}
            {filterMode === FILTER_MODES.CLOSED && isPartialClosed && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex flex-wrap gap-2 items-center justify-between" role="status">
                    <div className="text-xs text-amber-800 flex items-center gap-2">
                        <Icon glyph={ICONS.alert} size="sm" />
                        <span>{`Showing closed contracts returned since ${closedSince || 'recently'}. Older contracts are loaded a page at a time.`}</span>
                    </div>
                    <Button variant="link" size="sm" onClick={loadAllClosed}>
                        Load older (next 500)
                    </Button>
                </div>
            )}

            {picked && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 flex flex-wrap gap-2 items-center justify-between" role="status">
                    <span className="text-xs text-red-800 font-medium">
                        {`Showing ${plural(rows.length, 'open contract', 'open contracts')} (${SCOPE[branchFilter] || SCOPE.All}) with ${OWED_WHAT[picked]} not on Ejar yet - most owed first. The other filters are set aside.`}
                    </span>
                    <Button variant="link" size="sm" onClick={() => setPicked(null)}>Show every contract</Button>
                </div>
            )}

            <TableContainer maxHeight="70vh" className="overflow-auto">
                <Table sticky className="w-full">
                    <TableHead>
                        <TableRow>
                            <TableHeader className="w-10">#</TableHeader>
                            {headers.map(h => (
                                <TableHeader key={h.key} className="whitespace-nowrap">
                                    {h.label}
                                </TableHeader>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headers.length + 1}>
                                    <EmptyState icon={ICONS.airport} title="No airport contracts" description="No contracts match the current filters or search." />
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((row, idx) => {
                            const { isExpired, isToday } = getExpiryStatus(row, today);
                            // Expired = red row, drop-off today = amber row (status meaning kept).
                            const rowBg = isExpired ? 'bg-red-50' : isToday ? 'bg-amber-50' : 'bg-white';
                            const expiryTone = isExpired ? 'danger' : isToday ? 'warning' : undefined;

                            return (
                                <TableRow key={row.id || idx} className={rowBg}>
                                    <TableCell className={`sticky left-0 ${rowBg}`}><CellNumber value={idx + 1} /></TableCell>
                                    <TableCell>
                                        <a
                                            href="https://ejarprod.iyelo.com:6300//app/rental/contracts"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleCopy(row.contractNo, `Contract ${row.contractNo} copied!`);
                                                window.open(e.currentTarget.href, "_blank");
                                            }}
                                            className="hover:underline"
                                        >
                                            <CellId>{row.contractNo}</CellId>
                                        </a>
                                    </TableCell>
                                    <TableCell><CellId>{row.bookingNumber}</CellId></TableCell>
                                    <TableCell>
                                        {row.customer ? (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setCustomerProfile({ name: row.customer, phone: row.phone, contractNo: row.contractNo, plate: row.plateNo, model: row.model, branch: row.pickupBranch, status: row.status, customerType: row.customerType, pickupDate: formatDisplayDate(row.pickupDate), dropoffDate: formatDisplayDate(row.dropoffDate) }); }}
                                                className="hover:underline"
                                                title={`View ${row.customer}'s history`}
                                            >
                                                <CellText title={row.customer} tone={isExpired ? 'danger' : undefined}>{row.customer}</CellText>
                                            </button>
                                        ) : <CellText tone={isExpired ? 'danger' : undefined}>{row.customer}</CellText>}
                                    </TableCell>
                                    <TableCell><CellId quickKind="identity">{idOf(row.contractNo)}</CellId></TableCell>
                                    <TableCell>
                                        {(() => { const n = finesOf(row.contractNo); if (n === null) return <span className="ds-cell-empty">…</span>; return n > 0 ? <Badge variant="danger" size="sm">{n}</Badge> : <CellNumber value={0} />; })()}
                                    </TableCell>
                                    {(() => {
                                        const t = tripsOf(row.contractNo);
                                        return (
                                            <>
                                                <TableCell><TripCell cell={tripCellOf(t === undefined ? undefined : (t && t.gates) || null, { unlimited: !!(t && t.unlimitedSalik) })} /></TableCell>
                                                <TableCell><TripCell cell={tripCellOf(t === undefined ? undefined : (t && t.parking) || null, { unit: PARKING_UNIT })} /></TableCell>
                                            </>
                                        );
                                    })()}
                                    <TableCell><ContactPhone contractNo={row.contractNo} fallback={row.phone} /></TableCell>
                                    <TableCell><CellText>{row.pickupBranch}</CellText></TableCell>
                                    <TableCell>
                                        <div className="flex flex-col gap-1 items-center">
                                            {platesOf(row).map((p, pi) => (
                                                <CellPlate key={pi} tone={isExpired ? 'danger' : undefined}>{p}</CellPlate>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell><CellText>{row.model}</CellText></TableCell>
                                    <TableCell><span className="ds-cell-date">{formatDisplayDate(row.pickupDate)}</span></TableCell>
                                    <TableCell><span className={`ds-cell-date ${toneClass(expiryTone)}`}>{formatDisplayDate(row.dropoffDate)}</span></TableCell>
                                    <TableCell>
                                        <StatusBadge
                                            status={isExpired ? 'Expired' : row.status}
                                            tone={row.status === 'Open' ? (isExpired ? 'danger' : isToday ? 'warning' : 'success') : 'neutral'}
                                        />
                                    </TableCell>
                                    <TableCell><CellText>{row.createdBy}</CellText></TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <CustomerProfileModal open={!!customerProfile} seed={customerProfile} onClose={() => setCustomerProfile(null)} onCopy={showNotification} />
        </div>
    );
};

export default AirportContracts;
